import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, Globe, MapPin, Instagram, Facebook, MessageCircle, UserPlus, Linkedin } from "lucide-react";
import { FOUNDER, CONTACT, LOGO } from "../data/site";
import { generateVCard } from "../lib/vcard";

export default function DigitalCard() {
  const profileData = {
    name: FOUNDER.name,
    position: "Founder & Director",
    company: "The Thinkstitute",
    email: CONTACT.email,
    phone: "+91 92270 08998",
    personalPhone: "+91 76005 08998",
    website: "https://www.thethinkstitute.in",
    address: "305, J9 High Street, Vesu, Surat, Gujarat 395007",
    mapLink: "https://share.google/csr3bMJGCccv06eJO",
    companyInstagram: "https://www.instagram.com/the.thinkstitute.official/",
    personalInstagram: "https://www.instagram.com/think.with.chirag/",
    personalThreads: "https://www.threads.com/@think.with.chirag",
    facebook: "https://www.facebook.com/thethinkstitute",
    linkedin: "https://www.linkedin.com/in/chirag-kankaria-7b7654266/",
    companyLinkedin: "https://www.linkedin.com/company/the-thinkstitute/",
    whatsapp: `https://wa.me/919227008998`
  };
  const [socialsTab, setSocialsTab] = useState("company");

  useEffect(() => {
    const newTitle = "Chirag Kankaria - Founder & Director | The Thinkstitute";
    
    // Dynamically update document title
    const originalTitle = document.title;
    document.title = newTitle;

    // Update or create og:image
    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    const originalOgImage = ogImage.getAttribute('content');
    ogImage.setAttribute('content', FOUNDER.photo);

    // Update or create og:title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    const originalOgTitle = ogTitle.getAttribute('content');
    ogTitle.setAttribute('content', newTitle);

    return () => {
      // Cleanup on unmount
      document.title = originalTitle;
      if (originalOgImage) ogImage.setAttribute('content', originalOgImage);
      if (originalOgTitle) ogTitle.setAttribute('content', originalOgTitle);
    };
  }, []);

  const handleSaveContact = () => {
    generateVCard(profileData);
  };

  const LinkRow = ({ icon: Icon, imgSrc, imgSize = 22, title, subtitle, href, delay = 0 }) => (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-navy/10 hover:border-marigold/50 hover:shadow-lg transition-all group"
    >
      <div className="w-12 h-12 rounded-full bg-cream flex items-center justify-center text-teal-brand group-hover:bg-marigold group-hover:text-white transition-colors">
        {imgSrc ? (
          <div 
            className="bg-teal-brand group-hover:bg-white transition-colors"
            style={{ 
              width: imgSize,
              height: imgSize,
              WebkitMaskImage: `url('${imgSrc}')`, 
              WebkitMaskSize: 'contain', 
              WebkitMaskRepeat: 'no-repeat', 
              WebkitMaskPosition: 'center',
              maskImage: `url('${imgSrc}')`, 
              maskSize: 'contain', 
              maskRepeat: 'no-repeat', 
              maskPosition: 'center' 
            }} 
          />
        ) : (
          <Icon size={22} />
        )}
      </div>
      <div className="flex-1">
        <p className="text-sm font-bold text-navy">{title}</p>
        <p className="text-xs text-navy/60 mt-0.5 line-clamp-1">{subtitle}</p>
      </div>
    </motion.a>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex justify-center pb-20">
      <div className="w-full max-w-md bg-[#F5F5F7] min-h-screen shadow-2xl sm:min-h-0 sm:mt-10 sm:rounded-[2.5rem] sm:border sm:border-navy/10 overflow-hidden relative">
        
        {/* Background Accent */}
        <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-br from-marigold to-sky-500 rounded-b-[3rem]" />
        
        <div className="relative px-6 pt-12 pb-8 flex flex-col items-center text-center">
          {/* Profile Photo */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="w-[200px] h-[200px] rounded-full border-4 border-[#F5F5F7] shadow-xl overflow-hidden bg-white mb-4 z-10"
          >
            <img 
              src={FOUNDER.photo} 
              alt={FOUNDER.name} 
              className="w-full h-full object-cover object-top"
            />
          </motion.div>

          {/* Details */}
          <motion.h1 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-display font-extrabold text-3xl text-navy"
          >
            {profileData.name}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="text-marigold font-bold mt-1"
          >
            {profileData.position}
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-2 mt-2 text-navy/60 font-medium"
          >
            <img src={LOGO.full} alt="The Thinkstitute" className="h-4 object-contain" />
            <span>{profileData.company}</span>
          </motion.div>

        </div>

        {/* Links Section */}
        <div className="px-6 pb-12 space-y-3">
          <LinkRow 
            icon={Phone} 
            title="Mobile" 
            subtitle={profileData.phone} 
            href={`tel:${profileData.phone}`} 
            delay={0.4} 
          />
          <LinkRow 
            icon={Mail} 
            title="Email" 
            subtitle={profileData.email} 
            href={`mailto:${profileData.email}`} 
            delay={0.45} 
          />
          <LinkRow 
            icon={Globe} 
            title="Website" 
            subtitle="www.thethinkstitute.in" 
            href={profileData.website} 
            delay={0.5} 
          />
          <LinkRow 
            icon={MapPin} 
            title="Address" 
            subtitle={profileData.address} 
            href={profileData.mapLink} 
            delay={0.55} 
          />
          <LinkRow 
            imgSrc="/Social%20Media%20Logos/whatsappwhite.png"
            imgSize={33}
            title="WhatsApp" 
            subtitle="+91 92270 08998" 
            href={profileData.whatsapp} 
            delay={0.6} 
          />

          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="pt-2"
          >
            <button 
              onClick={handleSaveContact}
              className="w-full bg-navy text-white font-bold py-4 px-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-navy/90 active:scale-95 transition-all shadow-md"
            >
              <UserPlus size={20} />
              Save Contact
            </button>
          </motion.div>
          
          <div className="pt-6 pb-3">
            <div className="flex bg-navy/5 p-1 rounded-xl">
              <button 
                onClick={() => setSocialsTab("company")}
                className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${socialsTab === "company" ? "bg-white text-navy shadow-sm" : "text-navy/50 hover:text-navy/70"}`}
              >
                The Thinkstitute
              </button>
              <button 
                onClick={() => setSocialsTab("personal")}
                className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${socialsTab === "personal" ? "bg-white text-navy shadow-sm" : "text-navy/50 hover:text-navy/70"}`}
              >
                My Socials
              </button>
            </div>
          </div>
          
          <AnimatePresence mode="wait">
            {socialsTab === "company" ? (
              <motion.div 
                key="company"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                <LinkRow 
                  imgSrc="/Social%20Media%20Logos/linkedin-app-white-icon.png"
                  title="LinkedIn" 
                  subtitle="The Thinkstitute" 
                  href={profileData.companyLinkedin} 
                />
                <LinkRow 
                  imgSrc="/Social%20Media%20Logos/instagram-white-icon.png"
                  title="Instagram" 
                  subtitle="@the.thinkstitute.official" 
                  href={profileData.companyInstagram} 
                />
                <LinkRow 
                  imgSrc="/Social%20Media%20Logos/facebook-app-round-white-icon.png"
                  title="Facebook" 
                  subtitle="The Thinkstitute" 
                  href={profileData.facebook} 
                />
              </motion.div>
            ) : (
              <motion.div 
                key="personal"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                <LinkRow 
                  icon={Phone}
                  title="Personal Phone" 
                  subtitle={profileData.personalPhone} 
                  href={`tel:${profileData.personalPhone}`} 
                />
                <LinkRow 
                  imgSrc="/Social%20Media%20Logos/linkedin-app-white-icon.png"
                  title="LinkedIn" 
                  subtitle="Chirag Kankaria" 
                  href={profileData.linkedin} 
                />
                <LinkRow 
                  imgSrc="/Social%20Media%20Logos/instagram-white-icon.png"
                  title="Instagram" 
                  subtitle="@think.with.chirag" 
                  href={profileData.personalInstagram} 
                />
                <LinkRow 
                  imgSrc="/Social%20Media%20Logos/threads-white-icon.png"
                  title="Threads" 
                  subtitle="@think.with.chirag" 
                  href={profileData.personalThreads} 
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Footer */}
        <div className="text-center pb-8 pt-4">
          <img src={LOGO.full} alt="The Thinkstitute" className="h-6 object-contain mx-auto" />
        </div>
      </div>
    </div>
  );
}
